import { AIProvider } from '@newfront-insurance/ai-api';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { useCallback } from 'react';

import { useAdminTRPC } from '../../../shared/providers/trpc';

/**
 * Hook for triggering AI provider file upload and indexing after a file is committed to the Files service.
 *
 * The returned triggerUpload function is async — callers should await it.
 * If the upload fails, a warning is logged and the error is swallowed so the caller can continue.
 */
export function useUploadToOpenAI() {
  const { useMutation } = useAdminTRPC();
  const { mutateAsync } = useMutation('ai.uploadToAiProvider');
  const analytics = useAnalytics();

  const triggerUpload = useCallback(
    async (fileUuid: string, chatUuid?: string): Promise<void> => {
      analytics.track({
        event: 'Nova External AI Provider File Upload and Index Started',
        properties: { fileUuid, chatUuid, provider: AIProvider.OPENAI },
      });

      const startedAt = Date.now();

      try {
        await mutateAsync({ fileUuid, chatUuid, provider: AIProvider.OPENAI });
        const durationMs = Date.now() - startedAt;
        analytics.track({
          event: 'Nova External AI Provider File Upload and Index Completed',
          properties: { fileUuid, chatUuid, provider: AIProvider.OPENAI, durationMs },
        });
      } catch (error: unknown) {
        const durationMs = Date.now() - startedAt;
        browserLogger.warn(`[Nova] AI provider upload and index failed — processFiles will fall back to uploading`, {
          fileUuid,
          error: error instanceof Error ? error.message : String(error),
        });
        analytics.track({
          event: 'Nova External AI Provider File Upload and Index Failed',
          properties: {
            fileUuid,
            chatUuid,
            provider: AIProvider.OPENAI,
            durationMs,
            error: error instanceof Error ? error.message : String(error),
          },
        });
      }
    },
    [mutateAsync, analytics],
  );

  return { triggerUpload };
}
